import { injectable, injectFromBase } from 'inversify';
import { getI18n } from 'react-i18next';
import SettingsService from '@jwp/ott-common/src/services/SettingsService';
import type { Settings } from '@jwp/ott-common/types/settings';
import { OTT_GLOBAL_PLAYER_ID } from '@jwp/ott-common/src/constants';
import env from '@jwp/ott-common/src/env';
import { AppError } from '@jwp/ott-common/src/utils/error';

@injectable()
@injectFromBase({ extendConstructorArguments: true })
export default class NoIniSettingsService extends SettingsService {
  override isValidConfigSource = (source: string, settings: Settings) => {
    // Dynamic values are valid as long as they are defined
    if (settings.UNSAFE_allowAnyConfigSource) {
      return !!source;
    }

    return settings.defaultConfigSource === source;
  };

  override initialize = async () => {
    const settings: Settings = {
      UNSAFE_allowAnyConfigSource: __mode__ === 'demo' || __mode__ === 'dev' || __mode__ === 'test',
      playerId: OTT_GLOBAL_PLAYER_ID,
    };

    // The ini file values will be used if provided, even if compile-time values are set
    settings.defaultConfigSource ||= env.APP_DEFAULT_CONFIG_SOURCE;
    settings.playerId ||= env.APP_PLAYER_ID;
    settings.playerLicenseKey ||= env.APP_PLAYER_LICENSE_KEY;

    // This will result in an unusable app
    if (
      !settings.defaultConfigSource &&
      (!settings.additionalAllowedConfigSources || settings.additionalAllowedConfigSources?.length === 0) &&
      !settings.UNSAFE_allowAnyConfigSource
    ) {
      const i18n = getI18n();

      const errorPayload = {
        title: i18n.t('error:settings_invalid'),
        description: i18n.t('error:check_your_settings'),
        helpLink: 'https://github.com/jwplayer/ott-web-app/blob/develop/docs/initialization-file.md',
      };

      throw new AppError('No usable config sources', errorPayload);
    }

    return settings;
  };
}
